  // src/hooks/useLenisScrollSnap.ts
  import { useEffect } from "react";

  // Global holders to prevent duplicate instances
  let lenisInstance: any = null;
  let rafId: number | null = null;

  export const useLenisScrollSnap = (): void => {
    useEffect(() => {
      if (typeof window === "undefined" || typeof document === "undefined") return;

      // Respect reduced motion users
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

      let createdHere = false;

      const initLenis = async () => {
        // Dynamically import to avoid bundling Lenis & GSAP in main chunk
        const [{ default: Lenis }, gsapModule, scrollTriggerModule] = await Promise.all([
          import("@studio-freight/lenis"),
          import("gsap"),
          import("gsap/ScrollTrigger"),
        ]);

        const gsap = gsapModule.gsap || gsapModule.default || gsapModule;
        const ScrollTrigger = scrollTriggerModule.ScrollTrigger || scrollTriggerModule.default;

        // Register plugin only once
        const gsapAny = gsap as any;
        if (!gsapAny._lenisScrollRegistered) {
          gsapAny.registerPlugin(ScrollTrigger);
          gsapAny._lenisScrollRegistered = true;
        }

        // If we already have a Lenis instance, reuse it
        if (!lenisInstance) {
          // Cast options to any to satisfy TypeScript when API typings differ
          lenisInstance = new Lenis({ smooth: true } as any);
          createdHere = true;
        }

        // Start RAF loop (only once)
        if (!rafId) {
          const raf = (time: number) => {
            lenisInstance?.raf(time);
            rafId = requestAnimationFrame(raf);
          };
          rafId = requestAnimationFrame(raf);
        }

        // Proper scroller element
        const scroller = (document.scrollingElement || document.documentElement) as HTMLElement;

        // Setup scroller proxy for ScrollTrigger
        ScrollTrigger.scrollerProxy(scroller, {
          scrollTop(value?: number): number {
            if (value !== undefined) {
              lenisInstance.scrollTo(value, { immediate: true });
            }
            return scroller.scrollTop || window.scrollY;
          },
          getBoundingClientRect(): DOMRect {
            return {
              top: 0,
              left: 0,
              width: window.innerWidth,
              height: window.innerHeight,
              right: window.innerWidth,
              bottom: window.innerHeight,
              x: 0,
              y: 0,
              toJSON: () => "",
            };
          },
          pinType: scroller.style.transform ? "transform" : "fixed",
        });

        lenisInstance.on("scroll", () => ScrollTrigger.update());
        ScrollTrigger.defaults({ scroller });
        ScrollTrigger.refresh();
      };

      initLenis();

      return () => {
        // Only clean up if we created it here
        if (createdHere && lenisInstance) {
          lenisInstance.destroy?.();
          lenisInstance = null;
        }

        if (rafId) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }

        try {
          const gsap = (window as any).gsap;
          const ST = gsap?.ScrollTrigger;
          ST?.getAll?.().forEach((t: any) => t.kill());
          ST?.refresh?.();
        } catch (err) {
          // ignore cleanup errors
        }
      };
    }, []);
  };
